/** Predictive search modeled on Horizon's component: results are rendered server-side by sections/predictive-search.liquid and fetched through the Section Rendering API, then injected as-is (no morph). Keeps tresse's non-modal <dialog> under the header; public API: open(), close(), toggle(), reset(). */

import { Component } from "@theme/component";
import { debounce } from "@theme/utilities";
import { sectionRenderer } from "@theme/section-renderer";
import { ThemeEvents } from "@theme/events";

/** Section file rendered by the Section Rendering API; it needs no instance on the page. */
const SECTION_ID = "predictive-search";
/** Section rendered on /search for the full result count (sections/predictive-search-count.liquid). */
const COUNT_SECTION_ID = "predictive-search-count";

class PredictiveSearchComponent extends Component {
  // Only the refs the component genuinely cannot work without are required, so a stale or partial render degrades gracefully instead of throwing.
  requiredRefs = ["dialog", "searchInput", "predictiveSearchResults"];

  /** @type {AbortController | null} Staleness token for the in-flight section fetch. */
  #activeFetch = null;

  /** @type {string} Cached server-rendered empty state HTML. */
  #emptyStateHTML = "";

  /** Track whether last navigation was keyboard (for focus styling). */
  #isKeyboardNavigation = false;

  /** @type {number} Scroll position saved while the background scroll is locked. */
  #scrollY = 0;
  /** @type {boolean} True while <body> itself is frozen (mobile lock), so the close path only restores what it set. */
  #bodyFrozen = false;

  connectedCallback() {
    super.connectedCallback();

    this.#emptyStateHTML = this.refs.predictiveSearchResults.innerHTML;

    document.addEventListener("keydown", this.#onGlobalKeyDown);
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.addEventListener("announcement-bar:close", this.#onAnnouncementBarClose);
    this.refs.dialog.addEventListener("click", this.#onBackdropClick);
    this.refs.dialog.addEventListener("close", this.#onDialogClose);
    this.refs.searchInput.addEventListener("input", this.#onInput);
    this.refs.searchInput.addEventListener("keydown", this.#onSearchKeyDown);
    this.refs.closeButton?.addEventListener("click", () => this.close());
    this.refs.resetButton?.addEventListener("click", () => this.reset());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener("keydown", this.#onGlobalKeyDown);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.removeEventListener("announcement-bar:close", this.#onAnnouncementBarClose);
    this.#activeFetch?.abort();
    // Safety net: if the component is removed while open, don't leave the
    // page stuck with scroll disabled or the header forced opaque.
    if (this.refs.dialog?.open) {
      this.#unlockScroll();
      document.documentElement.classList.remove("search-open");
    }
  }

  // --- Public API ---

  open() {
    if (this.refs.dialog.open) return;
    // The panel is placed right under the pinned header by measuring its real bottom on open, which also covers an announcement bar that has scrolled away; the CSS falls back to --header-height.
    this.#setTopOffset();
    // Non-modal: the panel shows under the header (no top layer, no native backdrop) and show() keeps the dialog API.
    this.refs.dialog.show();
    // Verrouille le scroll de l'arrière-plan + force le header opaque tant que
    // la recherche est ouverte. Restaurés sur l'événement 'close' du dialog.
    this.#lockScroll();
    document.documentElement.classList.add("search-open");
    requestAnimationFrame(() => this.refs.searchInput.focus());
  }

  close() {
    if (!this.refs.dialog.open) return;
    this.refs.dialog.close();
  }

  toggle() {
    this.refs.dialog.open ? this.close() : this.open();
  }

  reset() {
    this.#activeFetch?.abort();
    this.refs.searchInput.value = "";
    this.refs.searchInput.setAttribute("aria-expanded", "false");
    this.refs.searchInput.focus();
    this.#hideResetButton();
    this.#showEmptyState();
  }

  // --- Input & keyboard ---

  #onInput = debounce(() => {
    const term = this.refs.searchInput.value.trim();

    if (!term) {
      this.#activeFetch?.abort();
      this.#hideResetButton();
      this.refs.searchInput.setAttribute("aria-expanded", "false");
      this.#showEmptyState();
      return;
    }

    this.#showResetButton();
    this.#fetchResults(term);
  }, 200);

  #onSearchKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      this.close();
      return;
    }

    const items = this.#items;
    const total = items.length;
    if (!total || event.key === "ArrowLeft" || event.key === "ArrowRight") return;

    const current = this.#currentIndex;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        this.#isKeyboardNavigation = true;
        this.#currentIndex = current < total - 1 ? current + 1 : 0;
        break;

      case "ArrowUp":
        event.preventDefault();
        this.#isKeyboardNavigation = true;
        this.#currentIndex = current > 0 ? current - 1 : total - 1;
        break;

      case "Tab":
        event.preventDefault();
        this.#isKeyboardNavigation = true;
        this.#currentIndex = event.shiftKey ? (current > 0 ? current - 1 : total - 1) : current < total - 1 ? current + 1 : 0;
        break;

      case "Enter": {
        if (current >= 0) {
          event.preventDefault();
          items[current].querySelector("a")?.click();
        }
        // Else let the form submit naturally to /search?q=...
        break;
      }
    }
  };

  /** CMD+K / Ctrl+K toggles the panel from anywhere; Escape closes it. */
  #onGlobalKeyDown = (event) => {
    if ((event.metaKey || event.ctrlKey) && event.key === "k") {
      event.preventDefault();
      this.toggle();
      return;
    }
    // Un dialog non-modal ne se ferme pas sur Échap nativement → on le gère
    // (utile quand le focus n'est pas dans l'input : résultat, bouton fermer…).
    if (event.key === "Escape" && this.refs.dialog.open) {
      this.close();
    }
  };

  /** Click on the dialog backdrop closes it. */
  #onBackdropClick = (event) => {
    if (event.target === this.refs.dialog) this.close();
  };

  #onDialogClose = () => {
    this.#unlockScroll();
    document.documentElement.classList.remove("search-open");
    this.reset();
  };

  /** A quick-add from a result card opens the cart drawer, which would otherwise stack under the panel. */
  #onCartUpdate = () => {
    this.close();
  };

  // --- Result navigation ---

  get #items() {
    return Array.from(this.refs.predictiveSearchResults.querySelectorAll(".predictive-search-results__item"));
  }

  get #currentIndex() {
    return this.#items.findIndex((el) => el.getAttribute("aria-selected") === "true");
  }

  set #currentIndex(index) {
    const items = this.#items;
    if (!items.length) return;

    items.forEach((item, i) => {
      if (i === index) {
        item.setAttribute("aria-selected", "true");
        if (this.#isKeyboardNavigation) item.classList.add("keyboard-focus");
        item.scrollIntoView({ block: "nearest", behavior: "smooth" });
      } else {
        item.removeAttribute("aria-selected");
        item.classList.remove("keyboard-focus");
      }
    });
  }

  // --- Fetching & rendering ---

  /**
   * Fetches sections/predictive-search.liquid rendered for the term and injects it.
   * @param {string} term
   */
  async #fetchResults(term) {
    const url = new URL(`${window.Shopify.routes.root}search/suggest`, window.location.origin);
    url.searchParams.set("q", term);
    // Colonne gauche = suggestions de requêtes + collections ; colonne droite =
    // product cards. Pages et articles ne sont pas affichés, on ne les demande pas.
    url.searchParams.set("resources[type]", "query,product,collection");
    url.searchParams.set("resources[limit_scope]", "each");
    url.searchParams.set("resources[options][unavailable_products]", "last");
    url.searchParams.set("resources[options][fields]", "title,product_type,variants.title,vendor");

    const abortController = this.#createAbortController();

    try {
      // `false`: never serve a cached section for a search term, the response is term-specific.
      const [markup, totalLabel] = await Promise.all([
        sectionRenderer.getSectionHTML(SECTION_ID, false, url),
        this.#fetchTotalLabel(term),
      ]);
      if (abortController.signal.aborted) return;

      this.#inject(markup, totalLabel);
      this.refs.searchInput.setAttribute("aria-expanded", "true");
    } catch (error) {
      if (abortController.signal.aborted) return;
      console.error("[predictive-search] section fetch failed:", error);
      this.#showEmptyState();
    }
  }

  /**
   * Fetches the total result count of the /search page « view all » submits to (same q and options[prefix]), as a translated label.
   * @param {string} term
   * @returns {Promise<string | null>} null on failure or an empty total, so the capped predictive count stays.
   */
  async #fetchTotalLabel(term) {
    const url = new URL(`${window.Shopify.routes.root}search`, window.location.origin);
    url.searchParams.set("q", term);
    url.searchParams.set("options[prefix]", "last");

    try {
      const markup = await sectionRenderer.getSectionHTML(COUNT_SECTION_ID, false, url);
      const total = new DOMParser().parseFromString(markup, "text/html").querySelector("[data-search-total]");
      if (!total || !(Number(total.dataset.searchTotal) > 0)) return null;
      return total.textContent.trim();
    } catch (error) {
      console.error("[predictive-search] total count fetch failed:", error);
      return null;
    }
  }

  /**
   * Injects the section's children in place of the current results. No morph: the
   * nodes are moved in as-is, so `.predictive-search-results` stays a direct child
   * of the scroll container and the layout rules that depend on it still apply.
   * @param {string} markup
   * @param {string | null} [totalLabel] Full search total, replacing the capped count when known.
   */
  #inject(markup, totalLabel = null) {
    const { predictiveSearchResults } = this.refs;
    const parsed = new DOMParser().parseFromString(markup, "text/html");
    // The Section Rendering API wraps the output in #shopify-section-<id>; fall back to the body if Shopify ever returns it bare.
    const source = parsed.getElementById(`shopify-section-${SECTION_ID}`) ?? parsed.body;
    if (totalLabel) source.querySelectorAll("[data-search-count]").forEach((node) => (node.textContent = totalLabel));

    predictiveSearchResults.replaceChildren(...source.childNodes);
    predictiveSearchResults.scrollTo(0, 0);
  }

  #showEmptyState() {
    const { predictiveSearchResults } = this.refs;
    predictiveSearchResults.innerHTML = this.#emptyStateHTML;
    predictiveSearchResults.scrollTo(0, 0);
  }

  /** Aborts the previous cycle and returns the token the new one is checked against. */
  #createAbortController() {
    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();
    return this.#activeFetch;
  }

  // --- UI helpers ---

  #showResetButton() {
    if (this.refs.resetButton) this.refs.resetButton.hidden = false;
  }
  #hideResetButton() {
    if (this.refs.resetButton) this.refs.resetButton.hidden = true;
  }

  /** Same background lock as the mobile menu: overflow on <html> and Lenis paused via drawer:open, plus the fixed-body trick on mobile only (iOS ignores overflow alone, and the panel covers the page there). */
  #lockScroll() {
    this.#scrollY = window.scrollY;
    document.documentElement.style.overflow = "hidden";

    // Freezing <body> (fixed, or overflow:hidden on top of <html>) makes it a scroll container and un-pins every position:sticky it holds, which would visibly drop the full-height dyptique image behind the translucent desktop panel.
    this.#bodyFrozen = window.matchMedia("(max-width: 750px)").matches;
    if (this.#bodyFrozen) {
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.top = `-${this.#scrollY}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";
      document.body.style.width = "100%";
    }
    document.dispatchEvent(new CustomEvent("drawer:open"));
  }

  /** Restores the background scroll and restarts Lenis. */
  #unlockScroll() {
    document.documentElement.style.overflow = "";
    if (this.#bodyFrozen) {
      this.#bodyFrozen = false;
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.width = "";
      window.scrollTo(0, this.#scrollY || 0);
    }
    document.dispatchEvent(new CustomEvent("drawer:close"));
  }

  /** The bar is gone and the header has moved back to the very top: the open panel has to follow it. Deferred a frame so the header is re-laid-out before it is measured. */
  #onAnnouncementBarClose = () => {
    if (!this.refs.dialog?.open) return;
    requestAnimationFrame(() => this.#setTopOffset());
  };

  /** Publishes the panel's top offset (bottom of the pinned header plus any announcement bar) into --search-top-offset, read by the CSS to size the dialog. */
  #setTopOffset() {
    const header = document.querySelector(".header_wrap") || document.querySelector(".header");
    if (!header) return;
    const offset = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
    this.refs.dialog.style.setProperty("--search-top-offset", `${offset}px`);
  }
}

if (!customElements.get("predictive-search-component")) {
  customElements.define("predictive-search-component", PredictiveSearchComponent);
}
